import React, { useEffect, useState } from 'react';
import {
  Person as UserIcon,
  EmojiEvents as AwardIcon,
  Bolt as ZapIcon,
  Share as Share2Icon,
  CheckBox as CheckSquareIcon,
  Star as StarIcon,
  Menu as MenuIcon,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  Grid,
  CardContent,
  IconButton,
  Rating,
} from '@mui/material';

import {
    StyledContainer,
    StyledHeader,
    StyledContent,
    EliteTag,
    StatCard,
    AffiliateCodeContainer,
    AffiliateCode,
    StyledHomeIcon,
} from './MyStats.styles';
import { exitReact, getReliabilityStatus } from 'utils';
import { useHistory } from 'react-router-dom';
import { useActionApi, usePersistedState } from 'hooks';
import Api from 'api';
import FixedLoader from 'components/FixedLoader';
import styles from './components.module.scss';
import { entities } from 'reduxStore';
import { BasicHygienistScoreProps, HygienistProps } from 'utils/types';
interface StatCardProps {
  icon: React.ReactElement;
  title: string;
  value: string;
  subtext?: string;
}
interface MilestoneProps {
  title: string;
  description: string;
  completed: boolean;
}
const StatCardComponent: React.FC<StatCardProps> = ({ icon, title, value, subtext }) => (
  <StatCard>
    <CardContent>
      <Box display="flex" alignItems="center" mb={1}>
        {React.cloneElement(icon, { style: { color: 'var(--secondary-main)', marginRight: '8px' } })}
        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
      </Box>
      <Typography variant="h4" color={'var(--secondary-main)'} fontWeight="bold">{value}</Typography>
      {subtext && <Typography variant="body2" color="text.secondary">{subtext}</Typography>}
    </CardContent>
  </StatCard>
);
const Milestone: React.FC<MilestoneProps> = ({ title, description, completed }) => (
  <Box display="flex" alignItems="flex-start">
    <Box color={completed ? 'var(--secondary-main)' : '#e0e0e0'} mr={2} mt={0.5}>
      {completed ? <CheckSquareIcon /> : <StarIcon />}
    </Box>
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" color={completed ? 'var(--secondary-main)' : 'text.primary'}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">{description}</Typography>
    </Box>
  </Box>
);

const SendEmail = (firstName: string, lastName: string, affiliateCode: string) => {
    const subject = `You’ve got a good friend! ${firstName} ${lastName} wants you to know about TempStars! 🙌`;
    const body = `You know good people! ${firstName} ${lastName} wants to`
        + ` tell you about TempStars and has sent along their referral code - so if you're looking to pick`
        + ` up dental temping shifts with North America's fastest-growing and most-loved dental temping`
        + ` service, check out the link below!\n`
        + `https://app.tempstars.com/index.html?registrationCode=${affiliateCode}`
    if (window.cordova && window.cordova.platformId === 'ios') {
        // @ts-ignore
        cordova.plugins.email.open({
            to:      '',
            subject,
            body
        });
    } else {
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_system');
    }
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    } 

    // Fallback for browsers that don't support the clipboard API
    // https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/Interact_with_the_clipboard
    const tempInput = document.createElement('input');
    tempInput.value = text;
    document.body.appendChild(tempInput);
    tempInput.select();
    const success = document.execCommand('copy');
    document.body.removeChild(tempInput);
    if (!success) {
      throw new Error("execCommand('copy') failed");
    }
    return true;

  } catch(e) {
      console.error('Error copying to clipboard', e)
      return false
  }
}

interface ReliabilityScorePositionProps {
    position: number
    total_records: number
    weightedReliability: number
}

export const MyStats: React.FC = () => {
    const history = useHistory();
    const [stats, setStats] = useState<any>(null);
    const [cancellationStats, setCancellationStats] = useState<ReliabilityScorePositionProps>({} as ReliabilityScorePositionProps);

    let hygienist = usePersistedState<HygienistProps>({
		entityName: entities.hygienist
	})[0];

    const [statsLoading, , getStats] = useActionApi({
        apiMethod: Api.getHygienistPersonalStats,
        onSuccess: (data: any) => {
            setStats(data)
        },
    })
    
    const [hygienistStatsLoading, , getHygienistStats] = useActionApi({
        apiMethod: Api.getHygienistStats,
        onSuccess: (data: ReliabilityScorePositionProps) => {
            setCancellationStats(data)
        },
    })

    useEffect(() => {
        getStats({})
        getHygienistStats({})
    }, [])

    const onGoHome = () => {
        history.push('loading')
        exitReact('home')
    }

    let status = 'Active';

    if (hygienist?.status === 1) {
        status = 'Pro';
    }
    if (hygienist?.status === 2) {
        status = 'Elite';
    }

    const whatAffiliate = () => {
        const url = hygienist?.country === 1
        ? 'https://www.tempstars.com/affiliate-usa'
        : 'https://www.tempstars.com/affiliate-ca'
    
        // @ts-ignore
        window.Cordova ? cordova.InAppBrowser.open(
            url,
            '_system',
            'location=yes'
        ) : window.open(
            url,
            '_blank',
            'location=yes'
        )
    }

    const reliabolityScore = getReliabilityStatus(1 - (cancellationStats?.position / cancellationStats?.total_records))

    const shareAffiliateCode = async () => {

        const shareData = {
            title: "TempStars Affiliate Code",
            text: "Share your TempStars affiliate code!",
            url: `https://app.tempstars.com/index.html?registrationCode=${stats?.affiliateCode}`,
          };

        try {
          await navigator.share(shareData);
        } catch (err) {
            console.log('Error sharing affiliate code', err)
        }
    }

    return (
        <>
      <StyledContainer>
          <StyledHeader>
            <StyledHomeIcon onClick={onGoHome}/>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight="bold" mb={1}>Your TempStars Statistics</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>(only you can see this)</Typography>
            </Box>
            <div />
          </StyledHeader>
          
          <StyledContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box display="flex" alignItems="center">
                <UserIcon sx={{ fontSize: 40, color: 'var(--secondary-main)', marginRight: '12px' }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">{hygienist?.firstName} {hygienist?.lastName}</Typography>
                  <Typography variant="body2" color="text.secondary">Dental Professional</Typography>
                </Box>
              </Box>
              {/* T2-3221: Holly asked to remove that too for now: https://tempstars.atlassian.net/browse/T2-3211?focusedCommentId=16624
              <EliteTag>{status}</EliteTag>
              */}
            </Box>
            <Box mb={4}>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                You've completed {stats?.completed || 0} shifts and earned ${stats?.total_earned || 0}
              </Typography>
              <Box display="flex" alignItems="center">
              <Rating
                    name="statsRating"
                    value={stats?.starScore || 0}
                    readOnly
                    precision={0.25}
                    emptyIcon={<StarBorder className={styles.starIconInactive} />}
                    icon={<Star className={styles.starIconActive} />}
                />
                <Typography variant="h6" fontWeight="bold" ml={1}>{stats?.starScore || 0}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                (not all shifts get reviewed by offices)
              </Typography>
            </Box>
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} md={12}>
                <StatCardComponent icon={<ZapIcon />} title="Shifts Completed" value={stats?.completed} />
              </Grid>
              {/* T2-3221: Temporarly hide reliability score 
              <Grid item xs={12} md={6}>
                <StatCardComponent icon={<AwardIcon />} title="Reliability Score" value={reliabolityScore} />
              </Grid>
              */}
            </Grid>
            <Box mb={4}>
              <Milestone 
                title="10 + shifts completed" 
                description="Complete 10 or more shifts to get this box checked"
                completed={stats?.completed >= 10}
              />
              <Milestone 
                title="Top 25% For Great Feedback" 
                description={`Complete ${stats?.star5Pro || 0} temping shifts with great feedback to get this box checked.`}
                completed={stats?.criterias.stars5 == false && stats?.completed > 0}
              />
              {/* T2-3221: Temporarly hide reliability score & milestone
              <Milestone 
                title="Top 25% For Great Reliability" 
                description="Maintain high reliability scores across multiple shifts."
                completed={cancellationStats?.position / cancellationStats?.total_records <= 0.25}
              />
              */}
              <Milestone 
                title="Elite Status Achieved" 
                description="Welcome to the Best of the Best!"
                completed={hygienist?.status > 1}
              />
            </Box>
            <AffiliateCodeContainer>
              <Typography variant="h6" fontWeight="bold" mb={1}>Your Affiliate Code</Typography>
              <Typography variant="body2" color="primary" sx={{ textDecoration: 'underline', cursor: 'pointer' }} mb={1} onClick={whatAffiliate}>
                What's this?
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                (click on the code to copy it to the clipboard)
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="white" border={1} borderColor="divider" borderRadius={1} p={1} mb={1}>
                <AffiliateCode onClick={() => stats?.affiliateCode ? copyToClipboard(stats.affiliateCode) : null}>
                  {stats?.affiliateCode || ''}
                </AffiliateCode>
                <IconButton color="primary" onClick={shareAffiliateCode}>
                  <Share2Icon />
                </IconButton>
              </Box>
              <Button
                variant="contained"
                fullWidth color="primary"
                onClick={() => SendEmail(hygienist?.firstName, hygienist?.lastName, stats?.affiliateCode)}
                >
                Email Code
              </Button>
            </AffiliateCodeContainer>
          </StyledContent>
      </StyledContainer>

      <FixedLoader loading={statsLoading || hygienistStatsLoading} />
      </>
    );
}